import ShowTooltipText from '@/components/tooltip';
import { ExpandMore, InfoOutlined } from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import Accordion, {
  AccordionProps,
  accordionClasses,
} from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary, {
  accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import Link from 'next/link';
import { PropsWithChildren, ReactNode } from 'react';

export type AccordionItemProps = Omit<AccordionProps, 'title'> & {
  title?: ReactNode;
  link?: string;
  infoText?: string;
  showInfo?: boolean;
};

const AccordionItem = ({
  title,
  link,
  children,
  showInfo,
  infoText,
  ...rest
}: PropsWithChildren<AccordionItemProps>) => {
  return (
    <Accordion
      variant='outlined'
      {...rest}
      sx={{
        [`&.${accordionClasses.root}`]: {
          'borderRadius': 1,
          'overflow': 'hidden',
          ':before': {
            height: 0,
          },
        },
        [`& .${accordionSummaryClasses.content}`]: {
          transition: 'font-weight .18s linear',
        },
        [`& .${accordionClasses.expanded} .${accordionSummaryClasses.content}`]:
          {
            fontWeight: '700',
            // fontSize: 18,
            marginBottom: 0,
          },
        backgroundColor: 'custom.accordionItem.backgroundColor',
        color: 'custom.accordionItem.textColor',
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMore sx={{ color: 'custom.accordionItem.titleColor' }} />
        }
        color='custom.accordionItem.titleColor'
      >
        <Stack
          direction='row'
          alignItems='center'
          gap={0.5}
          width='100%'
        >
          {link ? (
            <Link
              href={link}
              rel='noopener noreferrer'
            >
              {title}
            </Link>
          ) : (
            title
          )}
          {showInfo && infoText && (
            <Box
              onClick={(e) => {
                e?.stopPropagation();
              }}
            >
              <ShowTooltipText tooltipContent={infoText}>
                <InfoOutlined fontSize='small' />
              </ShowTooltipText>
            </Box>
          )}
        </Stack>
      </AccordionSummary>
      {children && (
        <AccordionDetails sx={{ whiteSpace: 'pre-line' }}>
          {children}
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default AccordionItem;
